import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const LINKS = [
  { to: '/',            label: 'Home'        },
  { to: '/marketplace', label: 'Marketplace' },
  { to: '/academy',     label: 'Academy'     },
  { to: '/about',       label: 'Ecosystem'   },
  { to: '/news',        label: 'News'        },
  { to: '/contact',     label: 'Contact'     },
];

export default function Nav() {
  const { user, logout }        = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [dark, setDark]         = useState(true);
  const location = useLocation();
  const menuRef  = useRef();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setUserMenu(false); }, [location]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    setDark(mq.matches);
    const handler = e => setDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handler = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setUserMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActive = (to) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  return (
    <>
      <nav style={{
        position:'fixed', top:0, left:0, right:0, zIndex:200,
        height:'var(--nav-h)',
        display:'flex', alignItems:'center',
        padding:'0 clamp(20px,5vw,60px)',
        background: scrolled
          ? (dark?'rgba(8,10,13,0.92)':'rgba(255,253,248,0.92)')
          : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
        transition:'background 0.3s, border-color 0.3s',
      }}>

        {/* Logo */}
        <Link to="/" style={{ display:'flex', alignItems:'center', gap:10, flex:1, textDecoration:'none' }}>
          <picture style={{ display:'flex', alignItems:'center' }}>
            <source srcSet="/logos/logo-light.png" media="(prefers-color-scheme: light)"/>
            <img src="/logos/logo-dark.png" alt="MilleHills"
              style={{ height:36, width:'auto', objectFit:'contain' }}
              onError={e=>{ e.target.style.display='none'; e.target.parentElement.nextSibling.style.display='flex'; }}/>
          </picture>
          <div style={{ display:'none', alignItems:'center', gap:10 }}>
            <div style={{ width:34, height:34, background:'var(--gold)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontWeight:700, fontSize:18, color:'#080a0d' }}>M</div>
          </div>
          <div>
            <div style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:'1rem', color:'var(--t-primary)', lineHeight:1.1 }}>MilleHills</div>
            <div style={{ fontSize:'0.6rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'var(--t-muted)' }}>Gisenyi · Rwanda</div>
          </div>
        </Link>

        {/* Desktop links */}
        <div style={{ display:'flex', alignItems:'center', gap:2 }} className="desktop-nav">
          {LINKS.map(l => (
            <Link key={l.to} to={l.to} style={{
              padding:'8px 14px', fontSize:'0.875rem',
              fontWeight: isActive(l.to) ? 600 : 400,
              color: isActive(l.to) ? 'var(--gold)' : 'var(--t-gold)',
              borderRadius:6, transition:'color 0.15s', textDecoration:'none',
            }}
              onMouseEnter={e=>{ if(!isActive(l.to)) e.currentTarget.style.color='var(--t-primary)'; }}
              onMouseLeave={e=>{ e.currentTarget.style.color=isActive(l.to)?'var(--gold)':'var(--t-gold)'; }}
            >{l.label}</Link>
          ))}
        </div>

        {/* Right side */}
        <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:16 }}>


          {user ? (
            <div ref={menuRef} style={{ position:'relative' }}>
              <button onClick={()=>setUserMenu(v=>!v)} style={{
                display:'flex', alignItems:'center', gap:8,
                padding:'6px 12px 6px 6px', borderRadius:24,
                background: userMenu?'var(--bg-2)':'rgba(232,164,58,0.08)',
                border:'1px solid rgba(232,164,58,0.25)',
                cursor:'pointer', transition:'all 0.15s',
              }}>
                <div style={{ width:28, height:28, borderRadius:'50%', background:'rgba(232,164,58,0.15)', border:'1px solid rgba(232,164,58,0.35)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontWeight:700, fontSize:12, color:'var(--gold)' }}>
                  {user.avatar_url
                    ? <img src={user.avatar_url.startsWith('http')?user.avatar_url:`${process.env.REACT_APP_MEDIA_URL||'http://localhost:5001'}${user.avatar_url}`} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                    : user.name?.[0]?.toUpperCase()||'U'
                  }
                </div>
                <span style={{ fontSize:'0.82rem', color:'var(--t-primary)', fontWeight:500, maxWidth:100, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {user.name?.split(' ')[0]}
                </span>
                <span style={{ fontSize:10, color:'var(--t-muted)' }}>▾</span>
              </button>

              {userMenu && (
                <div style={{
                  position:'absolute', top:'calc(100% + 8px)', right:0,
                  background: dark?'rgba(19,22,27,0.98)':'rgba(247,242,232,0.98)',
                  border:'1px solid var(--border)', borderRadius:12,
                  backdropFilter:'blur(16px)', minWidth:200, padding:'8px',
                  boxShadow:'0 8px 32px rgba(0,0,0,0.3)', animation:'fadeIn 0.15s ease',
                }}>
                  <div style={{ padding:'8px 12px 12px', borderBottom:'1px solid var(--border)', marginBottom:6 }}>
                    <div style={{ fontSize:'0.85rem', fontWeight:600, color:'var(--t-primary)' }}>{user.name}</div>
                    <div style={{ fontSize:'0.75rem', color:'var(--t-muted)', marginTop:2 }}>{user.email}</div>
                  </div>
                  {[
                    { to:'/dashboard',   label:'⬡ My Dashboard' },
                    { to:'/dashboard',   label:'◈ My Certificates', state:{ tab:'certificates' } },
                    { to:'/academy',     label:'◐ Browse Programs' },
                    { to:'/marketplace', label:'◈ Marketplace' },
                  ].map(item => (
                    <Link key={item.label} to={item.to} state={item.state}
                      style={{ display:'block', padding:'8px 12px', fontSize:'0.85rem', color:'var(--t-secondary)', borderRadius:8, transition:'all 0.1s', textDecoration:'none' }}
                      onMouseEnter={e=>{ e.currentTarget.style.background='var(--bg-2)'; e.currentTarget.style.color='var(--t-primary)'; }}
                      onMouseLeave={e=>{ e.currentTarget.style.background='transparent'; e.currentTarget.style.color='var(--t-secondary)'; }}
                    >{item.label}</Link>
                  ))}
                  <div style={{ borderTop:'1px solid var(--border)', marginTop:6, paddingTop:6 }}>
                    <button onClick={logout}
                      style={{ display:'block', width:'100%', padding:'8px 12px', fontSize:'0.85rem', color:'var(--red)', textAlign:'left', background:'none', border:'none', cursor:'pointer', borderRadius:8, transition:'background 0.1s' }}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(240,96,96,0.08)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                    >Sign Out</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display:'flex', gap:8 }} className="desktop-nav">
              <Link to="/login"
                style={{ padding:'8px 16px', fontSize:'0.85rem', color:'var(--t-secondary)', borderRadius:6, transition:'color 0.15s', textDecoration:'none' }}
                onMouseEnter={e=>e.currentTarget.style.color='var(--t-primary)'}
                onMouseLeave={e=>e.currentTarget.style.color='var(--t-secondary)'}
              >Sign In</Link>
              <Link to="/contact" className="btn btn-primary" style={{ padding:'9px 20px', fontSize:'0.85rem' }}>Get Started</Link>
            </div>
          )}

          <button onClick={()=>setOpen(v=>!v)} className="hamburger"
            style={{ display:'none', flexDirection:'column', gap:5, padding:8, borderRadius:6, background:open?'var(--bg-2)':'transparent', border:'none', cursor:'pointer' }}>
            {[0,1,2].map(i=>(
              <span key={i} style={{ width:20, height:1.5, background:'var(--t-secondary)', borderRadius:1, display:'block', transition:'transform 0.2s', transform:open?(i===0?'rotate(45deg) translate(4px,4px)':i===2?'rotate(-45deg) translate(4px,-4px)':'scaleX(0)'):'none' }}/>
            ))}
          </button>
        </div>

        <style>{`
          @media (max-width: 768px) {
            .desktop-nav { display: none !important; }
            .hamburger   { display: flex !important; }
          }
        `}</style>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div style={{ position:'fixed', top:'var(--nav-h)', left:0, right:0, bottom:0, background:dark?'rgba(8,10,13,0.97)':'rgba(255,253,248,0.97)', zIndex:199, padding:'40px 24px', display:'flex', flexDirection:'column', gap:4, backdropFilter:'blur(20px)', animation:'fadeIn 0.2s ease', overflowY:'auto' }}>
          {LINKS.map(l => (
            <Link key={l.to} to={l.to}
              style={{ padding:'14px 0', fontFamily:'var(--font-display)', fontSize:'1.6rem', fontWeight:600, color:isActive(l.to)?'var(--gold)':'var(--t-secondary)', borderBottom:'1px solid var(--border)', textDecoration:'none' }}
              className="fade-up">
              {l.label}
            </Link>
          ))}
          <div style={{ marginTop:24, display:'flex', flexDirection:'column', gap:10 }}>

            {user ? (
              <>
                <Link to="/dashboard" className="btn btn-outline" style={{ justifyContent:'center' }}>My Dashboard</Link>
                <button onClick={logout} style={{ padding:'12px', borderRadius:'var(--r-sm)', background:'transparent', border:'1px solid var(--border)', color:'var(--t-muted)', fontSize:'0.9rem', cursor:'pointer' }}>Sign Out</button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-outline" style={{ justifyContent:'center' }}>Sign In</Link>
                <Link to="/contact" className="btn btn-primary" style={{ justifyContent:'center' }}>Get Started →</Link>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
